/* eslint-disable react-hooks/exhaustive-deps */

import { ThemeProvider } from "@emotion/react";
import { useQuery } from "@tanstack/react-query";
import { Spin } from "antd";
import { AuthApi } from "api/auth";
import { useGetBalanceDV, useGetProfile } from "api/user/query";
import { QueryKeys } from "constants/queryKeys";
import { BalanceType } from "types/user";
import { handleLogout } from "helpers/handleLogout";
import { closeLoadingApp, openLoadingApp } from "helpers/loadingApp";
import { queryClient } from "index";
import { Suspense, useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { isMobile } from "router";
import GlobalStyles from "styles/global";
import { theme } from "styles/theme";

function App() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const username = localStorage.getItem("username") || "";
  const token = localStorage.getItem("token");
  const prevPath = useRef<string>("");

  !!username && queryClient.setQueryData([QueryKeys.username], username);
  !!token && queryClient.setQueryData([QueryKeys.token], token);

  const { data: tokenCache } = useQuery([QueryKeys.token]);
  const { data: usernameCache } = useQuery([QueryKeys.username]);
  const { data: isAuthenticated } = useQuery([QueryKeys.isAuthenticated]);
  useGetProfile(username, !!username);
  const { data: balance, refetch: refetchBalance } = useGetBalanceDV();

  useEffect(() => {
    localStorage.setItem("i18nextLng", "ct");
    localStorage.setItem("i18nLng", "ct");
    const element = document.getElementsByTagName("html");
    if (element && element[0]) {
      element[0].lang = "ct";
    }
  }, []);

  const [refresh, setRefresh] = useState(false);
  useQuery({
    queryKey: ["fetchToken", usernameCache, tokenCache],
    queryFn: async () => {
      if (!tokenCache) return "Token invalid";
      if (!usernameCache) return "username invalid";
      try {
        const response = await AuthApi.verifyToken(usernameCache as any, {
          username: usernameCache as any,
          token: tokenCache as any,
        });

        if (response.status !== 0) {
          handleLogout();
          navigate("/");
        } else {
          if (!isAuthenticated) {
            queryClient.setQueryData([QueryKeys.isAuthenticated], true);
          }
        }
        return response;
      } catch (error) {
        return error;
      }
    },
    enabled: tokenCache ? true : false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "token") {
        window.location.reload();
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    openLoadingApp();
    setTimeout(() => {
      closeLoadingApp();
    }, 1000);
  }, []);

  useEffect(() => {
    if (pathname !== prevPath.current) {
      sessionStorage.setItem("historyPath", prevPath.current);
      prevPath.current = pathname;
    }
  }, [pathname]);

  useEffect(() => {
    setRefresh(true);
    setTimeout(() => {
      setRefresh(false);
    }, 500);
  }, ["ct"]);

  useEffect(() => {
    if (!balance || !balance.total) return;
    const totalBalance = localStorage.getItem("totalBalance");
    if (totalBalance === balance.total) return;
    localStorage.setItem("totalBalance", balance.total);
  }, [balance]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "totalBalance") {
        const updatedValue = e.newValue;
        if (!updatedValue) return;

        const currentBalance = queryClient.getQueryData<BalanceType>([QueryKeys.GetBalanceDV]);
        if (currentBalance && currentBalance.total !== updatedValue) {
          refetchBalance()
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  return (
    <Suspense
      fallback={
        <div
          style={{
            height: "100vh",
            width: "100vw",
            flexDirection: "column",
            position: "fixed",
            zIndex: 1020,
            background: "#fff",
            alignItems: "center",
            justifyContent: "center",
            display: "none",
          }}
          id="loading-app"
          className="center-box"
        >
          <img loading="lazy" style={{ width: 120 }} src="/logo.png" alt="" />
          <Spin />
        </div>
      }
    >
      <div>
        <ThemeProvider theme={theme}>
          <GlobalStyles />
          <div
            style={{
              height: "100vh",
              width: "100vw",
              flexDirection: "column",
              position: "fixed",
              zIndex: 1020,
              background: "#fff",
              alignItems: "center",
              justifyContent: "center",
              display: "none",
            }}
            id="loading-app"
            className="center-box"
          >
            <img loading="lazy" style={{ width: 120 }} src="/logo.png" alt="" />
            <Spin />
          </div>
          {!refresh && <Outlet />}
        </ThemeProvider>
      </div>
    </Suspense>
  );
}

export default App;
